/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-len */
import { motion, useSpring, useTransform, useInView, AnimatePresence, cubicBezier, useAnimate, stagger } from 'framer-motion';

import { useRef, useState, useEffect, useContext } from 'react';
import { useInViewScroll, MotionImage, useSanitizedTranslation, XumoButton as Button, StackedContent, RemoteBlock, TextBlock, Banner, Prospect } from 'ui';
import { useScrollLock } from '../../hooks/useScrollLock';
import { VideoPlayer } from './VideoPlayer';
import { useMeasure, useWindowSize } from '@uidotdev/usehooks';
import { PageContext } from 'ui/contexts';

function getElementTopPosition(elem) {
  var box = elem.getBoundingClientRect();

  var body = document.body;
  var docEl = document.documentElement;

  var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  var scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  var clientTop = docEl.clientTop || body.clientTop || 0;
  var clientLeft = docEl.clientLeft || body.clientLeft || 0;

  var top = box.top + scrollTop - clientTop;
  var left = box.left + scrollLeft - clientLeft;

  return top;
}

//export type HeroProps = {};

function wrapWordsWithSpan(htmlString) {
  // Regex to match HTML tags or words/punctuation
  const regex = /(<[^>]+>)|([\w\p{P}']+)/gu;

  return htmlString.replace(regex, (match, tag, word) => {
    if (tag) return tag; // Don't wrap HTML tags
    return `<span>${word}</span>`; // Wrap words and punctuation
  });
}

export interface AboutHeroProps {
  title: string;
  body: string;
  img: string;
  mobileImg: string;
  cta: {
    label: string;
    href: string;
  };
}

const MobileAboutHero = ({ title, body, mobileImg }: AboutHeroProps) => {
  const { t } = useSanitizedTranslation();

  const { ref, progress: amt } = useInViewScroll([0, 1], ['start 100px', 'end 0vh']);

  const imageY = useTransform(amt, [0, 1.0], ['0%', '-5%'], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const swirlY = useTransform(amt, [0, 1], [0, 100], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const titleY = useTransform(amt, [0, 0.5], [0, 50]);

  const titleScale = useTransform(amt, [0, 0.5], [1, 0.9]);

  const [scope, animate] = useAnimate();

  useEffect(() => {
    animate(
      '.logo-img, h1 strong, h1 span',
      {
        y: [20, 0],
        opacity: [0, 1],
      },
      {
        ease: [0, 1, 1, 1],
        duration: 0.9,
        delay: stagger(0.075),
        deltaRest: 0.00001,
        deltaSpeed: 0.00001,
      },
    );
  }, []);

  return (
    <>
      <motion.section ref={ref} className="w-full bg-white text-xumoDarkPlum" data-testid={'keyart-section'}>
        {/* Swirl */}
        <div className="pointer-events-none relative -top-full z-0 -mt-[29vh] h-[23vh] w-full">
          <motion.div
            style={{ y: swirlY }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75, ease: [0, 1, 1, 1], duration: 0.9 }}
            className="absolute left-1/2 top-[0%] h-full origin-top -translate-x-1/2"
          >
            <div style={{ width: 1400, height: '130%' }} className="relative">
              <MotionImage
                src="/static/images/refresh/xumo-full-swirl-extended.png"
                priority
                fill
                sizes="100vw"
                className="mt-[460px] object-cover sm:mt-[500px] md:hidden"
              />
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { duration: 0.2 } }} ref={scope}>
          <div className="py-16 sm:px-4 xl:py-16">
            <motion.h1
              style={{ y: titleY, scale: titleScale }}
              className="type-title m-0 w-full text-center tracking-[-2.6px] [&>*]:inline-block"
              data-i18n
              dangerouslySetInnerHTML={{ __html: t(title) }}
            />
          </div>
          <motion.div className="wrapper aspect-[2560/2400] origin-top md:aspect-[2560/2000] lg:aspect-[2560/1282]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ease: [0, 1, 1, 1], delay: 0.55, duration: 0.6 }}
              className="relative"
            >
              <motion.div
                key="hero"
                style={{ opacity: 1 }}
                className="relative flex aspect-[2560/2400] w-full origin-top items-center justify-center overflow-clip rounded-2xl bg-xumoIce md:aspect-[2560/2000] lg:aspect-[2560/1282]"
              >
                <MotionImage
                  style={{ y: imageY, opacity: 1 }}
                  src={mobileImg}
                  priority
                  fill
                  sizes="150vw"
                  className="top-0 h-[105%] w-full object-cover object-bottom"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.section>
    </>
  );
};

const DesktopAboutHero = ({ title, img }: AboutHeroProps) => {
  const { t } = useSanitizedTranslation();

  const { headerHeight } = useContext(PageContext);
  const [heroRef, setHeroRef] = useState();
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [wrapperRef, { width: initialWidth, height: initialHeight }] = useMeasure();
  const [offsetTop, setOffsetTop] = useState(0);

  useEffect(() => {
    if (heroRef) {
      setOffsetTop(getElementTopPosition(heroRef));
    }
  }, [heroRef, setOffsetTop, windowHeight, windowWidth]);

  const scale = Math.max(((windowHeight || 1) - headerHeight) / ((initialHeight || 1) - headerHeight), (windowWidth || 1) / (initialWidth || 1));

  const { ref, progress: amt } = useInViewScroll([0, 1], ['start 100px', 'end 0vh']);

  const containerScale = useTransform(amt, [0, 0.4], [1.01, scale], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const heroPosition = useTransform(amt, [0, 0.3], [0, -offsetTop + 30 + headerHeight], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const imageY = useTransform(amt, [0, 1.0], ['0%', '-5%'], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const swirlY = useTransform(amt, [0, 1], [0, 100], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const imageOpacity = useTransform(amt, [0.5, 0.8], [1.0, 0], { ease: cubicBezier(0.35, 0, 0.44, 1) });

  const titleY = useTransform(amt, [0, 0.5], [0, -75]);

  const titleScale = useTransform(amt, [0, 0.5], [1, 0.9]);

  const [scope, animate] = useAnimate();
  const [bodyRef, bodyAnimate] = useAnimate();

  const bodyInView = useInView(bodyRef, { margin: '-30% 0% -30% 0%', once: true });

  useEffect(() => {
    animate(
      '.logo-img, h1 strong, h1 span',
      {
        y: [20, 0],
        opacity: [0, 1],
      },
      {
        ease: [0, 1, 1, 1],
        duration: 0.9,
        delay: stagger(0.075),
        deltaRest: 0.00001,
        deltaSpeed: 0.00001,
      },
    );
  }, []);

  return (
    <>
      <motion.section ref={ref} style={{ opacity: imageOpacity }} className="relative w-full bg-white text-xumoDarkPlum" data-testid={'keyart-section'}>
        {/* Swirl */}
        <div className="pointer-events-none sticky top-0 z-0 -mt-[12vh] flex h-[12vh] w-full justify-center">
          <motion.div
            style={{ y: swirlY }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75, ease: [0, 1, 1, 1], duration: 0.9, deltaRest: 0.00001, deltaSpeed: 0.00001 }}
            className="relative h-full md:top-[200px] lg:top-[400px] xl:top-[600px]"
          >
            <div style={{ width: 2400, height: 600, opacity: 1 }} className="rotate-180">
              <MotionImage fill priority src="/static/images/refresh/xumo-full-swirl-extended.png" style={{ objectFit: 'cover', objectPosition: 'center' }} />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ top: headerHeight, opacity: 1, transition: { duration: 0.2 } }}
          ref={scope}
          className="wrapper sticky h-0"
        >
          <div className="py-8 xl:py-16">
            <motion.h1
              style={{ y: titleY, scale: titleScale }}
              className="type-title m-0 text-center [&>*]:inline-block"
              data-i18n
              dangerouslySetInnerHTML={{ __html: t(title) }}
            />
          </div>
          <motion.div style={{ y: heroPosition }} ref={wrapperRef} className="aspect-[2560/1282] origin-top">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ease: [0, 1, 1, 1], delay: 0.55, duration: 0.6 }}
              className="relative"
            >
              <motion.div
                key="hero"
                ref={setHeroRef}
                style={{ opacity: 1, scale: containerScale }}
                className="relative flex aspect-[2560/1282] w-full origin-top items-center justify-center overflow-clip rounded-2xl bg-xumoIce"
              >
                <MotionImage
                  style={{ y: imageY, opacity: 1 }}
                  src={img}
                  priority
                  fill
                  sizes="150vw"
                  className="top-0 h-[105%] w-full object-cover object-bottom"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
        <div className="h-[250vh]" />
      </motion.section>
      <div className="relative z-40 -mt-[50vh] h-[400px] w-full" style={{ background: 'linear-gradient(to top, white 0%, rgba(255,255,255,0) 100%)' }} />
    </>
  );
};

export const AboutHero = ({ title, body, img, mobileImg, cta }: AboutHeroProps) => {
  const { label, href } = cta;
  const { t } = useSanitizedTranslation();

  const [wasMobile, setWasMobile] = useState(typeof window === 'undefined');
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  useScrollLock(!!videoSrc);

  const { width: windowWidth, height: windowHeight } = useWindowSize();

  const [bodyRef, bodyAnimate] = useAnimate();

  const bodyInView = useInView(bodyRef, { margin: '-30% 0% -30% 0%', once: true });

  useEffect(() => {
    if (!wasMobile && windowWidth !== null && windowWidth <= 768) {
      setWasMobile(true);
    }
  }, [wasMobile, windowWidth]);

  useEffect(() => {
    if (bodyInView) {
      bodyAnimate(
        '.body-ref, span',
        {
          y: [40, 0],
          opacity: [0, 1],
        },
        {
          ease: [0, 1, 1, 1],
          duration: 0.9,
          delay: stagger(0.03),
          deltaRest: 0.00001,
          deltaSpeed: 0.00001,
        },
      );
    }
  }, [bodyInView]);

  const formattedBody = wrapWordsWithSpan(body);

  return (
    <>
      {wasMobile ? (
        <MobileAboutHero title={title} body={body} img="" mobileImg={mobileImg} />
      ) : (
        <DesktopAboutHero title={title} body={body} img={img} mobileImg="" />
      )}
      <div className="relative z-40 w-full bg-white">
        <motion.div
          ref={bodyRef}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
          className="wrapper type-heading-alt pb-8 pt-8 text-center text-xumoDarkPlum [&>span]:inline-block [&>strong>span]:inline-block"
          data-i18n
          dangerouslySetInnerHTML={{ __html: t(formattedBody) }}
        />
        <div className="mt-8 flex justify-center md:mt-12 md:pb-16 2xl:pb-32">
          <Button
            label={label}
            submit
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setVideoSrc(href);
            }}
          />
        </div>
        {videoSrc && <VideoPlayer videoSrc={videoSrc} onClose={() => setVideoSrc(null)} />}
      </div>
    </>
  );
};
